import {
  REQUEST_ID_HEADER,
  mintRequestId,
  tagRequestId,
} from "@sill/shared/observability/requestId";
import { captureHandledError } from "@sill/shared/observability/captureHandledError";
import type { AgentSignals } from "@sill/shared/lib/signals-copy";
import type { PipelineSummary, ProHomeSummary } from "./pro-home-types";
import {
  freshSessionToken,
  mfaGate,
  type MfaReverificationLevel,
  type MfaTrigger,
} from "./auth/mfa-gate";

// Empty base routes through the Vite dev proxy (/api → :8080). In production
// the deployed origin serves /api directly, so an empty base is also correct.
export const API_BASE = import.meta.env.VITE_API_URL || "";

export type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  /** Clerk session JWT — pass the result of `getToken()`. */
  token?: string;
  signal?: AbortSignal;
  /** Internal: set on the single post-remedy retry so a second mfa_required
   *  blocks instead of looping. */
  mfaRetried?: boolean;
};

/**
 * ADR-006 safe-error envelope. Backend failures land as
 * `{"error":"<msg>","code":"<code>","request_id":"..."}`. We surface `.error`
 * as the user-facing message and keep `code` for dispatch.
 */
export interface ApiErrorBody {
  error?: string;
  code?: string;
  message?: string;
  request_id?: string;
  [key: string]: unknown;
}

export class ApiError extends Error {
  public code?: string;
  public requestId?: string;
  public body?: ApiErrorBody;

  constructor(
    public status: number,
    message: string,
    body?: ApiErrorBody,
  ) {
    super(message);
    this.name = "ApiError";
    this.body = body;
    this.code = body?.code;
    this.requestId = body?.request_id;
  }
}

/**
 * A 403 `mfa_required`. `trigger` says why the progressive gate fired
 * (spec 12.3): `acquisition` (the request brings clients or money in) or
 * `has_clients` (the account holds clients, so every route is gated).
 * ProAuthGate listens for it via `lib/auth/mfa-gate`.
 */
export class MfaRequiredError extends ApiError {
  public trigger: MfaTrigger;
  public reverification: MfaReverificationLevel | null;

  constructor(status: number, message: string, body?: ApiErrorBody) {
    super(status, message, body);
    this.name = "MfaRequiredError";
    this.trigger =
      body?.mfa_trigger === "acquisition" ? "acquisition" : "has_clients";
    const hint = (
      body?.clerk_error as
        { metadata?: { reverification?: MfaReverificationLevel } } | undefined
    )?.metadata?.reverification;
    this.reverification = hint ?? null;
  }
}

/**
 * Single fetch path for the Pro surface. Attaches the Clerk session token and
 * a fresh `X-Request-Id` (tagged onto the Sentry scope so a backend 5xx, its
 * log line, and the FE Sentry event share one id). `credentials: 'include'`
 * keeps Clerk's session cookie in play for same-origin proxying.
 */
export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, headers = {}, token, signal } = options;
  const isFormData = body instanceof FormData;

  const allHeaders: Record<string, string> = isFormData
    ? { ...headers }
    : { "Content-Type": "application/json", ...headers };

  if (token) {
    allHeaders["Authorization"] = `Bearer ${token}`;
  }

  const requestId = mintRequestId();
  tagRequestId(requestId);
  allHeaders[REQUEST_ID_HEADER] = requestId;

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: allHeaders,
    body: body ? (isFormData ? body : JSON.stringify(body)) : undefined,
    credentials: "include",
    signal,
  });

  if (!res.ok) {
    const err = await buildApiError(res);
    // mfa_required is an expected step-up prompt, not a defect; skip Sentry.
    if (err instanceof MfaRequiredError) {
      if (options.mfaRetried) {
        // Still refused after the remedy: stop at a stable blocked state.
        mfaGate.block(err);
        throw err;
      }
      // With a gate mounted this parks until the pro enrols / reverifies,
      // then retries once with a fresh token (fva just changed).
      if (await mfaGate.raise(err)) {
        const token = (await freshSessionToken()) ?? options.token;
        return apiRequest<T>(path, { ...options, token, mfaRetried: true });
      }
      throw err;
    }
    captureHandledError(`apiRequest:${path}`, err, { requestId });
    throw err;
  }

  if (res.status === 204) {
    return undefined as T;
  }

  return (await res.json()) as T;
}

async function buildApiError(res: Response): Promise<ApiError> {
  const text = await res.text().catch(() => "");
  const parsed = parseErrorBody(text);
  const message = errorMessage(text, parsed);
  if (res.status === 403 && parsed?.code === "mfa_required") {
    return new MfaRequiredError(res.status, message, parsed);
  }
  return new ApiError(res.status, message, parsed);
}

function parseErrorBody(text: string): ApiErrorBody | undefined {
  try {
    const parsed = JSON.parse(text) as ApiErrorBody;
    if (parsed && typeof parsed === "object") {
      return parsed;
    }
  } catch {
    // Non-JSON body — keep the raw text as the message.
  }
  return undefined;
}

// SILL-521 M2 cluster 4: the CRM entitlement-denial bodies carry ONLY
// `{"code",...}` — no `error`/`message` field — so the fallback below would
// otherwise surface the raw JSON body verbatim. Callers that want the richer
// toast + Upgrade CTA copy intercept `.code`/`.body` before ever reading
// `.message` (see `lib/entitlement-errors.ts`); this is the generic
// defense-in-depth fallback for any call site that doesn't.
const BARE_CODE_FALLBACK_MESSAGES: Record<string, string> = {
  crm_entitlement_required: "This feature requires Sill Pro.",
  contact_cap_exceeded: "You've reached the Free plan's contact limit.",
  send_limit_reached: "You've reached today's email limit.",
};

function errorMessage(text: string, body: ApiErrorBody | undefined): string {
  if (body?.error && body.error.trim() !== "") {
    return body.error;
  }
  // Structured errors use {"code","message"} (e.g. invalid_license,
  // duplicate_license) rather than {"error"}. Prefer the human message over the
  // raw JSON body so a code-carrying 4xx never surfaces verbatim to the user.
  if (body?.message && body.message.trim() !== "") {
    return body.message;
  }
  if (
    typeof body?.code === "string" &&
    BARE_CODE_FALLBACK_MESSAGES[body.code]
  ) {
    return BARE_CODE_FALLBACK_MESSAGES[body.code];
  }
  return text || "Request failed";
}

/** Typed verb helpers the views will use. Token is the Clerk session JWT. */
export function getJSON<T>(
  path: string,
  token?: string,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest<T>(path, { token, signal });
}

export function postJSON<T>(
  path: string,
  body: unknown,
  token?: string,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest<T>(path, { method: "POST", body, token, signal });
}

export function putJSON<T>(
  path: string,
  body: unknown,
  token?: string,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest<T>(path, { method: "PUT", body, token, signal });
}

export function patchJSON<T>(
  path: string,
  body: unknown,
  token?: string,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest<T>(path, { method: "PATCH", body, token, signal });
}

export function deleteJSON<T>(
  path: string,
  token?: string,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest<T>(path, { method: "DELETE", token, signal });
}

export const api = {
  get: getJSON,
  post: postJSON,
  put: putJSON,
  patch: patchJSON,
  delete: deleteJSON,
};

// ─────────────────────────────────────────────────────────────────────────────
// Marketplace agent-supply API (M4)
// ─────────────────────────────────────────────────────────────────────────────

export type MarketplaceDeliveryState = "none" | "pending_labeled" | "verified";
export type MarketplaceAvailabilityState =
  "active" | "offline_discoverable" | "off";
export type MarketplaceBuyerTier = "t1" | "t2" | "t3";
export type MarketplaceOfferStatus = "active" | "paused";

export interface MarketplaceVerification {
  id: string;
  license_num?: string;
  license_state?: string;
  brokerage?: string;
  google_place_id?: string;
  place_declined: boolean;
  status: string;
  rejection_reason?: string;
  review_note?: string;
  submitted_at: string;
  reviewed_at?: string;
  pending_extended_until?: string;
}

export interface MarketplaceAvailability {
  state: MarketplaceAvailabilityState;
  updated_at?: string;
}

export interface MarketplaceServiceZip {
  zip: string;
  city?: string;
  state?: string;
  slot: number;
}

export interface MarketplaceServiceArea {
  zips: MarketplaceServiceZip[];
  max: number;
}

export interface MarketplaceCatalogItem {
  type: string;
  label: string;
  description?: string;
  lane: "money" | "service";
  value_kind: "pct_of_price" | "dollars" | "non_cash";
  delivery_moment: string;
  ipc_relevant: boolean;
  featured?: boolean;
  display_order?: number;
}

export interface MarketplaceCapCell {
  available: boolean;
  price_cap_pct?: number;
  /** Retained for history — aggregate cash_budget_cents in tier_config supersedes (D4). */
  cap_cents?: number;
}

/**
 * Per-tier aggregate pricing config from the supply catalog (task 880/881).
 * Absent fields mean "no enforcement" — backward-compatible with old configs.
 */
export interface MarketplaceTierCell {
  /** Aggregate ceiling for all dollar-lane items combined (D4). */
  cash_budget_cents?: number;
  /** Optional minimum cash total; null/absent = no floor (D5). */
  cash_floor_cents?: number;
  /** Minimum number of qualifying commitments (T2=1, T3=2 recommended; 0/absent = no floor). */
  value_floor?: number;
}

export interface MarketplaceCatalog {
  config_id?: string;
  as_of?: string;
  change_source?: string;
  items: MarketplaceCatalogItem[];
  cap_matrix: Record<string, Record<MarketplaceBuyerTier, MarketplaceCapCell>>;
  /** Per-tier aggregate pricing config: cash_budget_cents, cash_floor_cents, value_floor (D4/D5). */
  tier_config?: Record<MarketplaceBuyerTier, MarketplaceTierCell>;
  state_gate_list: string[];
  reference_price_cents: number;
  max_primary_zips: number;
  stale_pending_days: number;
  derived_earn_up_to_cents: Record<MarketplaceBuyerTier, number>;
}

export interface MarketplaceOfferItem {
  type: string;
  value?: number;
}

export interface MarketplaceStandingOffer {
  id: string;
  tier: MarketplaceBuyerTier;
  items: MarketplaceOfferItem[];
  commission_pct?: number;
  status: MarketplaceOfferStatus;
  delivery_state: MarketplaceDeliveryState;
  created_at: string;
  updated_at: string;
}

export interface MarketplaceBlurb {
  text: string;
  max: number;
  updated_at?: string;
}

/**
 * The four self-asserted descriptors on the marketplace profile row (plan 444
 * M1). Codes, not labels: `@sill/shared/lib/descriptor-labels` renders them. Arrays are
 * `[]` and `licensed_since` an explicit null when unset, never omitted.
 */
export interface MarketplaceDescriptors {
  specialties: string[];
  languages: string[];
  designations: string[];
  licensed_since: number | null;
}

/**
 * Body of PATCH /api/marketplace/pro/descriptors. Each key is independent:
 * an absent key is untouched, an array replaces the stored set (`[]` clears),
 * and `licensed_since: null` clears the year. Each editor section sends only
 * its own key.
 */
export type MarketplaceDescriptorsPatch = Partial<MarketplaceDescriptors>;

export interface MarketplaceSetupView {
  verification?: MarketplaceVerification;
  blurb: MarketplaceBlurb;
  descriptors: MarketplaceDescriptors;
  delivery_state: MarketplaceDeliveryState;
  availability: MarketplaceAvailability;
  service_area: MarketplaceServiceArea;
  standing_offers: MarketplaceStandingOffer[];
  catalog: MarketplaceCatalog;
  // M11-A1: never omitempty on the wire — an explicit zero rating / empty
  // review list renders (see reviews.Summary/DisplayReview on the backend).
  review_summary: MarketplaceReviewSummary;
  reviews: MarketplaceDisplayReview[];
  /**
   * Plan 444 M4: the pro's own signals line, exactly what a buyer sees.
   * Both keys are omitted when the server could not resolve them.
   */
  signals?: AgentSignals;
  /** Floor copy ONLY for absent signals; `{}` means all are showing. */
  signal_floors?: MarketplaceSignalFloors;
  /**
   * What the agent typed at /join (plan 476), used only to open the
   * verification card filled in. Omitted when none; never auto-submitted.
   */
  verification_prefill?: MarketplaceVerificationPrefill;
  /**
   * Signup-time demand record: `false` = the agent's area is not live yet (CRM
   * works, marketplace cannot go live); `null`/absent = unknown. Always sent.
   */
  service_area_launched?: boolean | null;
}

export interface MarketplaceVerificationPrefill {
  license_num: string;
  license_state: string;
  brokerage: string;
}

export interface MarketplaceSignalFloors {
  closed_on_sill?: string;
  responsiveness_band?: string;
  licensed_years?: string;
}

export type MarketplaceReviewLabel = "worked_together" | "closed";
export type MarketplaceReviewOrigin = "marketplace" | "invited";

export interface MarketplaceReviewSummary {
  avg: number;
  count: number;
  closed_count: number;
}

export interface MarketplaceDisplayReview {
  /** The reviewer's name — present on the PRO's own received-reviews surface
   *  only (their own client). Absent on the public agent page, and absent
   *  here when identity couldn't be authorized; fall back to reviewer_line. */
  reviewer_name?: string;
  stars: number;
  body: string;
  label: MarketplaceReviewLabel;
  origin: MarketplaceReviewOrigin;
  created_at: string;
  reviewer_line: string;
}

export interface MarketplacePlaceCandidate {
  place_id: string;
  name: string;
  address: string;
}

export interface MarketplaceVerificationInput {
  license_num: string;
  /** Two-letter jurisdiction; the server defaults to WA when omitted. */
  license_state?: string;
  brokerage: string;
  place_id?: string;
  place_declined?: boolean;
}

export interface MarketplaceStandingOfferInput {
  items: MarketplaceOfferItem[];
  commission_pct?: number;
  status: MarketplaceOfferStatus;
}

export function getMarketplaceSetup(
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceSetupView> {
  return getJSON<MarketplaceSetupView>(
    "/api/marketplace/pro/setup",
    token,
    signal,
  );
}

export function submitMarketplaceVerification(
  input: MarketplaceVerificationInput,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceVerification> {
  return postJSON<MarketplaceVerification>(
    "/api/marketplace/pro/verification",
    input,
    token,
    signal,
  );
}

export function listMarketplacePlaceCandidates(
  q: string,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplacePlaceCandidate[]> {
  return getJSON<MarketplacePlaceCandidate[]>(
    `/api/marketplace/pro/verification/place-candidates?q=${encodeURIComponent(q)}`,
    token,
    signal,
  );
}

export function saveMarketplaceBlurb(
  text: string,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceBlurb> {
  return putJSON<MarketplaceBlurb>(
    "/api/marketplace/pro/blurb",
    { text },
    token,
    signal,
  );
}

export function patchMarketplaceDescriptors(
  patch: MarketplaceDescriptorsPatch,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceDescriptors> {
  return patchJSON<MarketplaceDescriptors>(
    "/api/marketplace/pro/descriptors",
    patch,
    token,
    signal,
  );
}

export function setMarketplaceAvailability(
  state: MarketplaceAvailabilityState,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceAvailability> {
  return putJSON<MarketplaceAvailability>(
    "/api/marketplace/pro/availability",
    { state },
    token,
    signal,
  );
}

export function setMarketplaceServiceArea(
  zips: string[],
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceServiceArea> {
  return putJSON<MarketplaceServiceArea>(
    "/api/marketplace/pro/service-area",
    { zips },
    token,
    signal,
  );
}

/** A single serviceable ZIP candidate returned by the typeahead endpoint. */
export interface ServiceAreaCandidate {
  zip: string;
  city: string;
  state: string;
}

/**
 * Typeahead for serviceable ZIPs / city names / neighborhoods.
 * Returns only serviceable candidates — never non-serviceable ZIPs.
 * `GET /api/marketplace/pro/service-area/typeahead?q=<string>`
 */
export function serviceAreaTypeahead(
  q: string,
  token?: string,
  signal?: AbortSignal,
): Promise<ServiceAreaCandidate[]> {
  const params = new URLSearchParams({ q });
  return getJSON<ServiceAreaCandidate[]>(
    `/api/marketplace/pro/service-area/typeahead?${params.toString()}`,
    token,
    signal,
  );
}

export function saveMarketplaceStandingOffer(
  tier: MarketplaceBuyerTier,
  input: MarketplaceStandingOfferInput,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceStandingOffer> {
  return putJSON<MarketplaceStandingOffer>(
    `/api/marketplace/pro/standing-offers/${tier}`,
    input,
    token,
    signal,
  );
}

export function deleteMarketplaceStandingOffer(
  tier: MarketplaceBuyerTier,
  token?: string,
  signal?: AbortSignal,
): Promise<void> {
  return deleteJSON<void>(
    `/api/marketplace/pro/standing-offers/${tier}`,
    token,
    signal,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Marketplace pro offers — browse / compose / withdraw / ledger (M5-D/M5-F)
//
// Mirrors backend/internal/services/marketplace/offers wire shapes
// (PassportCard, BrowsePage, ComposeContext, ProOfferRow, ValidationError).
// Every pro-facing DTO speaks `passport_id` HANDLES — never a raw profile
// UUID (tech-spec §Decisions "Pseudonymous passport handles").
// ─────────────────────────────────────────────────────────────────────────────

export type MarketplaceOfferStatusV2 =
  | "active"
  | "accepted"
  | "withdrawn"
  | "superseded"
  | "expired"
  | "closed_buyer_changed";

export type MarketplaceClosedBy = "pro" | "consumer" | "system";

export interface MarketplacePriceBracket {
  low: number;
  high: number;
  period?: string;
}

/** `low` is optional — SILL-30 removed the fabricated low, so post-SILL-30
 * passports carry only a `high`. Never default it to 0. */
export interface MarketplacePassportBuyingPower {
  low?: number;
  high: number;
  rate_percent?: number;
  rate_as_of?: string;
}

export interface MarketplacePassportLine {
  kind: string;
  state?: string;
  band?: MarketplacePriceBracket;
  provenance?: string;
  method?: string;
  verified_as_of?: string | null;
  basis?: string;
}

/** Pro-facing browse row / passport-detail DTO — handles + display code only. */
export interface MarketplacePassportCard {
  passport_id: string;
  display_code: string;
  tier?: MarketplaceBuyerTier;
  variant?: string;
  goal?: string;
  timeline?: string;
  target_area?: string;
  price_bracket?: MarketplacePriceBracket;
  price_kind?: "shopping_range" | "property_bracket" | "target_bracket";
  price_approximate?: boolean;
  activity_bucket?: string;
  buying_power?: MarketplacePassportBuyingPower;
  lines?: MarketplacePassportLine[];
  offer_out: boolean;
}

export interface MarketplaceBrowsePage {
  rows: MarketplacePassportCard[];
  next_cursor?: string;
  /** Server verdict on whether this pro can send an offer right now (always present). */
  can_send_offers?: boolean;
  /** Why not: `payment_method_missing`, omitted when sending is allowed. */
  send_blocker?: string;
}

export interface MarketplaceBrowseFilters {
  tier?: MarketplaceBuyerTier;
  goal?: string;
  timeline?: string;
  activity?: string;
  /** price bracket, applied client-side against `price_bracket` rows. */
  priceBracket?: string;
  /** "service_area" (default) | "all" — widening is an explicit act. */
  scope?: "service_area" | "all";
  /** "newest" (default) | "ready_to_act_first". */
  sort?: "newest" | "ready_to_act_first";
  cursor?: string;
  limit?: number;
}

export function browseMarketplacePassports(
  filters: MarketplaceBrowseFilters,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceBrowsePage> {
  const params = new URLSearchParams();
  if (filters.tier) params.set("tier", filters.tier);
  if (filters.goal) params.set("goal", filters.goal);
  if (filters.timeline) params.set("timeline", filters.timeline);
  if (filters.activity) params.set("activity", filters.activity);
  if (filters.scope) params.set("scope", filters.scope);
  if (filters.sort) params.set("sort", filters.sort);
  if (filters.cursor) params.set("cursor", filters.cursor);
  if (filters.limit) params.set("limit", String(filters.limit));
  const qs = params.toString();
  return getJSON<MarketplaceBrowsePage>(
    `/api/marketplace/pro/passports${qs ? `?${qs}` : ""}`,
    token,
    signal,
  );
}

export interface MarketplaceMyOfferState {
  offer_id?: string;
  status?: MarketplaceOfferStatusV2;
}

/** Missing or unknown runtime values must be handled as restricted. */
export type MarketplaceFinancialVisibility =
  "shared" | "restricted" | "unavailable";

/** Named connected-client status projection. This is absent from anonymous
 * marketplace discovery responses. */
export interface MarketplaceConnectedClient {
  display_name?: string;
  housing_situation?: string;
  lease_expiration?: string;
  published_at?: string;
  goal?: string;
  timeline?: string;
  target_area?: string;
  target_price_band?: string;
  profile_completeness?: number;
}

/** Present only when the server grants the current professional's financial
 * lens. Missing or unknown financial visibility must be treated as restricted. */
export interface MarketplaceClientFinancialPayload {
  household_income?: number;
  savings?: number;
  credit_score_range?: string;
  monthly_debts?: unknown;
  target_price?: number;
  budget_monthly?: number;
  reserve_floor_months?: number;
  comfort_dti_pct?: number;
  employment_months?: number;
  recent_job_gap?: boolean;
  has_second_job?: boolean;
  is_self_employed?: boolean;
  has_bonus_income?: boolean;
  has_commission_income?: boolean;
  affordability_band_low?: number;
  affordability_band_high?: number;
  sill_qual_price?: number;
  affordability_movement?: {
    band_high_from?: number;
    band_high_to?: number;
    as_of?: string;
  };
  down_payment_ready?: boolean;
  readiness_score?: number;
  readiness_segment?: string;
  readiness_label?: string;
  estimated_dti?: number;
  rate_watch_base_rate?: number;
  rate_watch_personalized_rate?: number;
  rate_watch_spread?: number;
  qual_monthly_min?: number;
  qual_monthly_max?: number;
}

export interface MarketplaceComposeContext {
  passport: MarketplacePassportCard;
  financial_visibility?: MarketplaceFinancialVisibility;
  client?: MarketplaceConnectedClient;
  financial?: MarketplaceClientFinancialPayload;
  /** Server-projected controls for a named client. This deliberately carries
   * caps and availability, never the buyer's verification tier. */
  offer_eligibility?: MarketplaceOfferEligibility;
  competing_count: number;
  best_rebate_pct?: number;
  best_cash_cents?: number;
  my_offer?: MarketplaceMyOfferState;
  recovery?: { window_id: string; client_display_name: string };
}

export interface MarketplaceOfferEligibility {
  items: Record<string, { available: boolean; price_cap_pct?: number }>;
  cash_budget_cents?: number;
}

export function getMarketplaceComposeContext(
  passportID: string,
  token?: string,
  signal?: AbortSignal,
  recoveryWindowID?: string,
): Promise<MarketplaceComposeContext> {
  if (recoveryWindowID) {
    return getJSON<MarketplaceComposeContext>(
      `/api/marketplace/pro/recovery-windows/${encodeURIComponent(recoveryWindowID)}/compose-context`,
      token,
      signal,
    );
  }
  // An empty id would request the `/passports/` collection path. Refuse before
  // any protected request instead.
  if (!passportID) {
    return Promise.reject(new Error("passport id is required"));
  }
  return getJSON<MarketplaceComposeContext>(
    `/api/marketplace/pro/passports/${encodeURIComponent(passportID)}`,
    token,
    signal,
  );
}

// Plan 377 M5 (REST surface, design spec §4.6): renamed from
// /clients/:profileID/compose-context — marketplace keeps its own
// profileID-keyed identity space, only losing the dead word "clients".
export function getMarketplaceBuyerComposeContext(
  profileID: string,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceComposeContext> {
  return getJSON<MarketplaceComposeContext>(
    `/api/marketplace/pro/buyers/${encodeURIComponent(profileID)}/compose-context`,
    token,
    signal,
  );
}

export interface MarketplaceComposeInput {
  passport_id: string;
  buyer_tier: MarketplaceBuyerTier;
  items: MarketplaceOfferItem[];
  commission_pct?: number;
  note: string;
  expiry_days: number;
  /** offer id being superseded — "" for a fresh compose (replace mode). */
  replaces?: string;
  recovery_window_id?: string;
  fee_quote?: MarketplaceFeeConsent;
}

export interface MarketplaceFeeConsent {
  rate_card_id: string;
  net_cents: number;
}

export interface MarketplaceComposedOffer {
  id: string;
  passport_id: string;
  buyer_tier: MarketplaceBuyerTier;
  items: MarketplaceOfferItem[];
  commission_pct?: number;
  note: string;
  status: MarketplaceOfferStatusV2;
  expires_at: string;
  superseded_by?: string;
  created_at: string;
}

export function composeMarketplaceOffer(
  input: MarketplaceComposeInput,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceComposedOffer> {
  return postJSON<MarketplaceComposedOffer>(
    "/api/marketplace/pro/offers",
    input,
    token,
    signal,
  );
}

/** Account-specific connection fee shown before a direct offer is sent. */
export function getMarketplaceConnectionFeeQuote(
  buyerTier: MarketplaceBuyerTier,
  passportID: string,
  token?: string,
  signal?: AbortSignal,
  /** Active offer a revise supersedes; it does not hold the signup promo. */
  replacesOfferID?: string,
): Promise<{ fee_quote: MarketplaceFeeQuote }> {
  const query = new URLSearchParams({
    buyer_tier: buyerTier,
    passport_id: passportID,
  });
  if (replacesOfferID) query.set("replaces", replacesOfferID);
  return getJSON<{ fee_quote: MarketplaceFeeQuote }>(
    `/api/marketplace/pro/connection-fee-quote?${query.toString()}`,
    token,
    signal,
  );
}

export interface MarketplaceWithdrawnOffer {
  id: string;
  status: MarketplaceOfferStatusV2;
  closed_by?: MarketplaceClosedBy;
  close_reason?: string;
  closed_at?: string;
}

export function withdrawMarketplaceOffer(
  offerID: string,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceWithdrawnOffer> {
  return postJSON<MarketplaceWithdrawnOffer>(
    `/api/marketplace/pro/offers/${offerID}/withdraw`,
    {},
    token,
    signal,
  );
}

export interface MarketplaceOfferItemLanes {
  rebate?: MarketplaceOfferItem[];
  cash?: MarketplaceOfferItem[];
  perks?: MarketplaceOfferItem[];
}

/** Pro ledger row — carries `passport_id` (handle), never `buyer_profile_id`. */
export interface MarketplaceProOfferRow {
  offer_id: string;
  passport_id: string;
  display_code: string;
  buyer_tier: MarketplaceBuyerTier;
  /** Frozen-at-compose target-area label (from the offer's own
   * passport_facts snapshot) — omitted for pre-M3 rows with no facts. */
  target_area?: string;
  price_bracket?: string;
  price_kind?: "shopping_range" | "property_bracket" | "target_bracket";
  price_approximate?: boolean;
  items: MarketplaceOfferItemLanes;
  commission_pct?: number;
  status: MarketplaceOfferStatusV2;
  is_expired: boolean;
  /** Active legacy rows whose send predates fee consent and must be resent. */
  needs_fee_consent: boolean;
  closed_by?: MarketplaceClosedBy;
  close_reason?: string;
  expires_at: string;
  superseded_by?: string;
  created_at: string;
}

export function getMarketplaceOffersLedger(
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceProOfferRow[]> {
  return getJSON<MarketplaceProOfferRow[]>(
    "/api/marketplace/pro/offers",
    token,
    signal,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Marketplace connections — pro requests inbox (plan 239, M6-B, task s1)
// ─────────────────────────────────────────────────────────────────────────────

/** Wire projection of connections.FeeQuote — mirrors feeQuoteDTO exactly. */
export interface MarketplaceFeeQuote {
  rate_card_id: string;
  cause: string;
  tier: string;
  list_cents: number;
  discount_cents: number;
  /** Label of the grant behind `discount_cents` (e.g. "First connection free"). */
  grant_label?: string;
  net_cents: number;
}

/**
 * Pro-facing pending standing-offer connection request row — mirrors
 * connectionRequestListDTO exactly (backend/internal/api/marketplace_connections.go).
 * The anonymity chokepoint: carries the buyer's pseudonymous passport handle
 * (`passport_id`/`display_code`), never the raw profile id, name, or contact.
 */
export interface MarketplaceConnectionRequest {
  id: string;
  role: string;
  buyer_tier: MarketplaceBuyerTier;
  status: string;
  passport_id: string;
  display_code: string;
  target_area?: string;
  timeline?: string;
  price_bracket?: MarketplacePriceBracket;
  price_kind?: "shopping_range" | "property_bracket" | "target_bracket";
  price_approximate?: boolean;
  activity_bucket?: string;
  /**
   * Frozen standing-offer item lanes (rebate/cash/perks) split out of the
   * request's StandingOfferSnapshot — mirrors connectionRequestListDTO
   * exactly (backend/internal/api/marketplace_connections.go). Never
   * exposes raw profile identity, only the offer's own economics.
   */
  item_lanes?: MarketplaceOfferItemLanes;
  commission_pct?: number;
  fee_quote: MarketplaceFeeQuote;
  fee_quote_available?: boolean;
  expires_at: string;
  created_at: string;
  /**
   * True when the buyer already holds an active link to this pro (invite,
   * referral, or an earlier connection). Confirming is then the $0 retention
   * path and the row is not anonymous. Optional only for deploy skew: absent
   * reads as a stranger's request.
   */
  own_client?: boolean;
  /** Set only on an own-client row whose link shares the buyer's name. */
  buyer_name?: string;
  /** The pro's own People row for this buyer (own-client rows only). */
  contact_id?: string;
}

/** Wire projection of a store.Connection — mirrors connectionDTO exactly. */
export interface MarketplaceConnection {
  id: string;
  role: string;
  cause: string;
  retention: boolean;
  status: string;
  buyer_tier: MarketplaceBuyerTier;
  fee_cents: number;
  discount_cents: number;
  amount_charged_cents?: number;
}

/** Wire projection of a store.ConnectionRequest — mirrors connectionRequestDTO exactly. */
export interface MarketplaceConnectionRequestSummary {
  id: string;
  role: string;
  buyer_tier: MarketplaceBuyerTier;
  status: string;
  decline_reason?: string;
}

/**
 * Marketplace-first Home summary (plan 443). Marketplace-gated: callers load
 * it only while the go-live gate is on (`stores/proHome.ts`).
 */
export function getProHomeSummary(
  token?: string,
  signal?: AbortSignal,
): Promise<ProHomeSummary> {
  return getJSON<ProHomeSummary>("/api/marketplace/pro/home", token, signal);
}

/** Pipeline stage counts split marketplace vs invited — a CRM read, not gated. */
export function getPipelineSummary(
  token?: string,
  signal?: AbortSignal,
): Promise<PipelineSummary> {
  return getJSON<PipelineSummary>(
    "/api/professional/pipeline-summary",
    token,
    signal,
  );
}

export function listMarketplaceProRequests(
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceConnectionRequest[]> {
  return getJSON<MarketplaceConnectionRequest[]>(
    "/api/marketplace/pro/requests",
    token,
    signal,
  );
}

/**
 * Wire projection for GET /api/marketplace/pro/requests/:id/detail —
 * mirrors requestDetailDTO exactly (backend/internal/api/marketplace_connections.go).
 * Widened for the M9.7 full-page request detail view: embeds the SAME
 * `MarketplaceConnectionRequest` fields the requests inbox list uses
 * (id/status/buyer_tier/fee_quote/expires_at/item_lanes/commission_pct) PLUS
 * the buyer's anonymized passport card — never the raw profile id, name, or
 * contact.
 */
export interface MarketplaceRequestDetail extends MarketplaceConnectionRequest {
  passport: MarketplacePassportCard;
}

export function getMarketplaceProRequestDetail(
  requestID: string,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceRequestDetail> {
  return getJSON<MarketplaceRequestDetail>(
    `/api/marketplace/pro/requests/${requestID}/detail`,
    token,
    signal,
  );
}

export function confirmMarketplaceProRequest(
  requestID: string,
  feeQuote: MarketplaceFeeConsent,
  token?: string,
  signal?: AbortSignal,
): Promise<{
  connection: MarketplaceConnection;
  fee_quote: MarketplaceFeeQuote;
}> {
  return postJSON<{
    connection: MarketplaceConnection;
    fee_quote: MarketplaceFeeQuote;
  }>(
    `/api/marketplace/pro/requests/${requestID}/confirm`,
    { fee_quote: feeQuote },
    token,
    signal,
  );
}

export function declineMarketplaceProRequest(
  requestID: string,
  reason: string,
  token?: string,
  signal?: AbortSignal,
): Promise<{ request: MarketplaceConnectionRequestSummary }> {
  return postJSON<{ request: MarketplaceConnectionRequestSummary }>(
    `/api/marketplace/pro/requests/${requestID}/decline`,
    { reason },
    token,
    signal,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Marketplace connections — pro connections ledger + disconnect (plan 239, M6-B, task s2)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Pro-facing ledger row for an active/pending/ended connection — mirrors
 * connectionLedgerDTO exactly (backend/internal/api/marketplace_connections.go).
 * `buyer_name`/`buyer_contact` are present only when `status === "active"`
 * (the reveal-on-activate gate); every other status leaves both keys off the
 * wire entirely (never null).
 *
 * `passport_id`/`display_code` (additive) carry the buyer's stable
 * pseudonymous passport handle on EVERY row — populated pre-reveal (the same
 * handle-only contract as `MarketplaceConnectionRequest`) and coexisting with
 * `buyer_name`/`buyer_contact` once the connection is active. Never the raw
 * profile id.
 *
 * `last_charge_error`/`retry_count`/`held_at` (M9 remediation, additive —
 * see connectionLedgerDTO's own doc comment) are the pro's own held-loop
 * send-state: a Stripe decline CODE (never a raw message), a retry counter,
 * and the timestamp the row entered `held`. All three are `omitempty` on
 * the wire and meaningful only on a held/charging row.
 *
 * `buyer_email`/`buyer_phone`/`profile_id` (M6-B M9.9, additive) close the
 * owed contact-reveal gap: all three are present ONLY alongside
 * `buyer_name` (`status === "active"`), mirroring its reveal-on-activate
 * gate exactly. `buyer_email` carries the same value as the pre-existing
 * `buyer_contact`, itemized under an unambiguous name for the reveal
 * modal's labeled rows. `profile_id` lets the FE deep-link an active row to
 * the buyer's client page (`/clients/{profile_id}`) without exposing the
 * raw profile UUID on any pre-reveal row.
 */
export interface MarketplaceConnectionLedgerRow {
  id: string;
  role: string;
  cause: string;
  retention: boolean;
  status: string;
  buyer_tier: MarketplaceBuyerTier;
  passport_id: string;
  display_code: string;
  fee_cents: number;
  discount_cents: number;
  amount_charged_cents?: number;
  fee_line: string;
  grant_label?: string;
  buyer_name?: string;
  buyer_contact?: string;
  buyer_email?: string;
  buyer_phone?: string;
  profile_id?: string;
  last_charge_error?: string;
  retry_count?: number;
  held_at?: string;
  created_at: string;
  ended_at?: string;
}

export function listMarketplaceProConnections(
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceConnectionLedgerRow[]> {
  return getJSON<MarketplaceConnectionLedgerRow[]>(
    "/api/marketplace/pro/connections",
    token,
    signal,
  );
}

export interface MarketplaceRefundCase {
  id: string;
  connection_id: string;
  status:
    | "review"
    | "approved"
    | "pending"
    | "completed"
    | "attention"
    | "declined"
    | "ineligible";
  eligibility: string;
  eligibility_reason: string;
  currency: string;
  full_remaining_cents: number | null;
  original_full_remaining_cents: number | null;
  balance_status: string;
  qualifying_event_at: string;
  decision: {
    decision: "approved" | "declined";
    created_at: string;
  } | null;
  execution: {
    outcome: "pending" | "succeeded" | "failed" | "uncertain";
    submitted_at: string | null;
    reconciled_at: string | null;
  } | null;
}

/** Customer-safe, payer-scoped refund case status. Provider identities and errors never cross this boundary. */
export async function listMarketplaceRefundCases(
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceRefundCase[]> {
  const response = await getJSON<{ cases: MarketplaceRefundCase[] }>(
    "/api/marketplace/pro/refund-cases",
    token,
    signal,
  );
  return response.cases;
}

/**
 * Scoped reveal-poll read: ONE connection row instead of the whole book
 * (simplify pass) — backs `useConnectionReveal`'s poll loop so a repeated
 * poll no longer re-fetches (and re-resolves the buyer identity of) every
 * OTHER connection in the pro's book on every tick.
 */
export function getMarketplaceProConnection(
  connectionID: string,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceConnectionLedgerRow> {
  return getJSON<MarketplaceConnectionLedgerRow>(
    `/api/marketplace/pro/connections/${connectionID}`,
    token,
    signal,
  );
}

/**
 * Wire projection of the pro-disconnect response
 * (PostMarketplaceProConnectionDisconnect) — mirrors the handler's response
 * body exactly. `outcome` is a fixed `"moved_to_contacts"` string; a pro
 * disconnect does not initiate a provider refund. Buyer disconnects create
 * an evidence-gated review case through a separate flow.
 */
export interface MarketplaceProDisconnectResult {
  connection: MarketplaceConnection;
  outcome: string;
}

export function disconnectMarketplaceConnection(
  connectionID: string,
  reason: string,
  note?: string,
  token?: string,
  signal?: AbortSignal,
): Promise<MarketplaceProDisconnectResult> {
  return postJSON<MarketplaceProDisconnectResult>(
    `/api/marketplace/pro/connections/${connectionID}/disconnect`,
    { reason, note },
    token,
    signal,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Message template types + API (SILL-390 M2a / M2b)
// ─────────────────────────────────────────────────────────────────────────────

/** The two envelope modes the backend supports (spec §"Content + template worlds"). */
export type EnvelopeMode = "relayed_pro_message" | "sill_notification";

/** Wire-format for a single saved template (newest version, owner-scoped). */
export interface MessageTemplate {
  id: string;
  version: number;
  channel: "email" | "sms";
  name: string;
  /** Recipient-facing email subject line (separate from `name`; empty falls back to `name` on send). */
  subject?: string;
  /** One-line description shown in picker / list. */
  description?: string;
  body: string;
  envelope_mode: EnvelopeMode;
  /**
   * Number of active automations currently using this template.
   *
   * Optional: the backend TemplateUsageReader is a no-op in v1 — the field
   * is omitted from the list API response. The UI must treat undefined / 0 as
   * "not in use" and render NO usage chip. The chip naturally lights up once a
   * real usage reader lands and starts returning counts.
   */
  usage_count?: number;
  created_at: string;
}

/** Payload for creating a new template. */
export interface CreateTemplateInput {
  channel: "email" | "sms";
  name: string;
  subject?: string;
  description?: string;
  body: string;
  envelope_mode: EnvelopeMode;
}

/** GET /api/professional/message-templates — wire: { templates: MessageTemplate[] } */
export async function listTemplates(
  token?: string,
  signal?: AbortSignal,
): Promise<MessageTemplate[]> {
  const res = await getJSON<{ templates: MessageTemplate[] }>(
    "/api/professional/message-templates",
    token,
    signal,
  );
  return res.templates;
}

/** POST /api/professional/message-templates — wire: { template: MessageTemplate } */
export async function createTemplate(
  input: CreateTemplateInput,
  token?: string,
  signal?: AbortSignal,
): Promise<MessageTemplate> {
  const res = await postJSON<{ template: MessageTemplate }>(
    "/api/professional/message-templates",
    input,
    token,
    signal,
  );
  return res.template;
}

/**
 * Payload for saving a new version of an existing template.
 * `version` is the current client-held version (for conflict detection).
 */
export interface SaveVersionInput {
  version: number;
  channel: "email" | "sms";
  name: string;
  subject?: string;
  description?: string;
  body: string;
  envelope_mode: EnvelopeMode;
}

/**
 * A single rule that references a template (wire shape: {kind, id, name}).
 * Matches the backend's `ruleRefResponse` struct.
 */
export interface RuleRef {
  kind: string;
  id: string;
  name: string;
}

/**
 * Response from PUT (SaveVersion).
 *
 * The backend embeds template fields at the TOP LEVEL (templateResponse is
 * embedded in saveVersionResponse, not wrapped under a "template" key).
 *
 * `edit_warning` is an ARRAY of {kind,id,name} rule refs ([]ruleRefResponse),
 * present when the template is currently used by one or more active automations.
 * Derive the count as `edit_warning.length` and rule names as `edit_warning.map(r => r.name)`.
 */
export interface SaveVersionResponse {
  // Template fields at top level (not under a "template" key)
  id: string;
  version: number;
  channel: string;
  name: string;
  subject?: string;
  body: string;
  envelope_mode: string;
  created_at: string;
  /** Non-null array of rule refs when the template is in use. */
  edit_warning?: RuleRef[];
}

/**
 * Response from DELETE when the template is in use (409 status). `rules`
 * contains the using-rule descriptors; the UI shows these in the restricted dialog.
 */
export interface DeleteRestrictedBody {
  rules: { id: string; name: string }[];
}

/** PUT /api/professional/message-templates/:id — SaveVersion */
export function saveTemplateVersion(
  id: string,
  input: SaveVersionInput,
  token?: string,
  signal?: AbortSignal,
): Promise<SaveVersionResponse> {
  return putJSON<SaveVersionResponse>(
    `/api/professional/message-templates/${id}`,
    input,
    token,
    signal,
  );
}

/** POST /api/professional/message-templates/:id/copy — Save as new (new id @ v1) — wire: { template: MessageTemplate } */
export async function saveTemplateAsNew(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<MessageTemplate> {
  const res = await postJSON<{ template: MessageTemplate }>(
    `/api/professional/message-templates/${id}/copy`,
    {},
    token,
    signal,
  );
  return res.template;
}

/** DELETE /api/professional/message-templates/:id — Delete (restricted while in use) */
export function deleteTemplate(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<void> {
  return deleteJSON<void>(
    `/api/professional/message-templates/${id}`,
    token,
    signal,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Campaigns types + API (SILL-396 / plan 61 M4 backend, M6 frontend)
// ─────────────────────────────────────────────────────────────────────────────

/** The Channel value matches messaging.Channel ("email" | "sms" | "in_app"). */
export type CampaignChannel = "email" | "sms" | "in_app";

/** Campaign lifecycle states — mirrors backend campaigns.CampaignStatus. */
export type CampaignStatus =
  | "draft"
  | "scheduled"
  | "sending"
  | "sent"
  | "partial"
  | "failed"
  | "canceled";

/**
 * A recipient reference. Mirrors backend messaging.RecipientRef. The JSON keys
 * are Go-default (capitalized) because the backend struct carries no json tags.
 * A linked client recipient sets ProfileID; a book contact sets ContactID.
 */
export interface RecipientRef {
  ContactID?: string;
  ProfileID?: string;
  Email?: string;
  Phone?: string;
}

/**
 * SegmentFilter mirrors backend messaging.SegmentFilter. The struct has no json
 * tags, so the wire keys are the Go-default capitalized field names. The FE
 * audience builder maps its AudienceState onto this via audienceStateToFilter.
 */
export interface CampaignSegmentFilter {
  Preset?: string;
  IncludeKind?: string;
  Timeline?: string;
  Tier?: string;
  Tags?: string[];
  ExcludeUnverified?: boolean;
  Search?: string;
}

/**
 * AudienceSpec mirrors backend campaigns.AudienceSpec. Filter is the segment;
 * Include/Exclude are explicit recipient overrides (Include refs are guaranteed
 * additive counts in Preview). When `explicit` is true the campaign sends to
 * EXACTLY the `include` set (the Enrollment shape) — the filter is ignored.
 */
export interface CampaignAudienceSpec {
  filter: CampaignSegmentFilter;
  include?: RecipientRef[];
  exclude?: RecipientRef[];
  explicit?: boolean;
}

/** Campaign wire shape — mirrors backend campaigns.Campaign. */
export interface Campaign {
  id: string;
  professional_id: string;
  role: string;
  name: string;
  status: CampaignStatus;
  audience: CampaignAudienceSpec;
  template_id: string;
  custom_subject: string;
  custom_body: string;
  // Effective message actually sent — the backend resolves `template_id` content
  // (saved or materialized template) on read, falling back to custom_*. Present
  // so the read-only viewer renders real content instead of "No body".
  effective_subject?: string;
  effective_body?: string;
  channel: CampaignChannel;
  envelope_mode: EnvelopeMode;
  scheduled_for?: string;
  workflow_id: string;
  run_id: string;
  sent_count: number;
  /** Recipients whose send terminally failed at the provider (drives `partial`). */
  failed_count: number;
  excluded_count: number;
  /**
   * Recipients still waiting for a later day's send budget (SEC-06). Computed
   * on read; absent on older payloads.
   */
  queued_count?: number;
  /** Next budget reset when the campaign is stalled on today's limit, else null. */
  resumes_at?: string | null;
  created_at: string;
  updated_at: string;
}

/** Aggregate, no-PII preview result — mirrors campaignPreviewResponse. */
export interface CampaignPreview {
  matched: number;
  eligible: number;
  excluded_by_reason: Record<string, number>;
  /**
   * Server-computed send schedule under the pro's daily campaign budget:
   * [{date: "YYYY-MM-DD", count}]. The backend owns these numbers — never
   * derive a spread in TS. Absent/empty = nothing to say.
   */
  spread?: CampaignSpreadDay[];
}

export interface CampaignSpreadDay {
  date: string;
  count: number;
}

/** Dispatch mode for POST /campaigns. */
export type CampaignMode = "send_now" | "schedule" | "draft";

/**
 * Body for POST /api/professional/campaigns. campaign_id is client-minted for
 * create-idempotency. The message is a stored template_id OR a custom
 * subject/body pair. scheduled_for is required for mode=schedule (RFC3339).
 */
export interface CampaignCreateInput {
  mode: CampaignMode;
  campaign_id: string;
  name?: string;
  audience: CampaignAudienceSpec;
  template_id?: string;
  custom_subject?: string;
  custom_body?: string;
  channel: CampaignChannel;
  envelope_mode: EnvelopeMode;
  scheduled_for?: string;
}

/** POST /api/professional/campaigns/preview → { matched, eligible, excluded_by_reason } */
export function previewCampaign(
  body: { audience: CampaignAudienceSpec; channel: CampaignChannel },
  token?: string,
  signal?: AbortSignal,
): Promise<CampaignPreview> {
  return postJSON<CampaignPreview>(
    "/api/professional/campaigns/preview",
    body,
    token,
    signal,
  );
}

/** POST /api/professional/campaigns → the created/updated campaign. */
export function createCampaign(
  input: CampaignCreateInput,
  token?: string,
  signal?: AbortSignal,
): Promise<Campaign> {
  return postJSON<Campaign>(
    "/api/professional/campaigns",
    input,
    token,
    signal,
  );
}

/** GET /api/professional/campaigns — wire: { campaigns: Campaign[] } */
export async function listCampaigns(
  token?: string,
  signal?: AbortSignal,
): Promise<Campaign[]> {
  const res = await getJSON<{ campaigns: Campaign[] }>(
    "/api/professional/campaigns",
    token,
    signal,
  );
  return res.campaigns ?? [];
}

/** GET /api/professional/campaigns/:id → the campaign. */
export function getCampaign(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<Campaign> {
  return getJSON<Campaign>(`/api/professional/campaigns/${id}`, token, signal);
}

/**
 * One campaign recipient's delivery outcome — mirrors backend
 * campaigns.RecipientStatus. Owner-scoped: the recipient ids the pro owns plus
 * the delivery status; never the rendered body or recipient email. Display
 * names are joined client-side from the loaded client/contact rosters.
 */
export interface CampaignRecipient {
  profile_id?: string;
  contact_id?: string;
  channel: CampaignChannel;
  /** "sent" | "failed" | "suppressed" | "sending" */
  status: string;
  suppress_reason?: string;
  sent_at?: string;
}

/** GET /api/professional/campaigns/:id/recipients — wire: { recipients: [] } */
export async function getCampaignRecipients(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<CampaignRecipient[]> {
  const res = await getJSON<{ recipients: CampaignRecipient[] }>(
    `/api/professional/campaigns/${id}/recipients`,
    token,
    signal,
  );
  return res.recipients ?? [];
}

/** POST /api/professional/campaigns/:id/duplicate → a fresh draft. */
export function duplicateCampaign(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<Campaign> {
  return postJSON<Campaign>(
    `/api/professional/campaigns/${id}/duplicate`,
    {},
    token,
    signal,
  );
}

/** POST /api/professional/campaigns/:id/cancel → the canceled campaign. */
export function cancelCampaign(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<Campaign> {
  return postJSON<Campaign>(
    `/api/professional/campaigns/${id}/cancel`,
    {},
    token,
    signal,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Notifications types + API (Plan A / C2 bell+panel)
// ─────────────────────────────────────────────────────────────────────────────

/** Matches proinbox.ContentType */
export type NotificationContentType = "text";

/** Matches proinbox.Content */
export interface NotificationContent {
  type: NotificationContentType;
  title: string;
  body: string;
}

/** Matches proinbox.ActionKind */
export type NotificationActionKind =
  | "contact"
  | "draft"
  | "listing"
  | "client_profile"
  | "campaign"
  | "automation"
  // Marketplace offer lifecycle (M5-E `ActionKindMarketplaceOffer`,
  // task:m5-actionkind-route) — deep-links to the pro's own offer ledger.
  | "marketplace_offer"
  // Payment repair lives in the stable Settings billing section.
  | "marketplace_billing"
  // Marketplace connection requests (`ActionKindMarketplaceRequests`,
  // M6-B M9.9 remediation) — deep-links to the pro's requests inbox. Was
  // missing from this union (and from `routeForAction`), so the "New
  // connection request" notification silently fell through to the
  // unmapped default instead of routing to `/marketplace/requests`.
  | "marketplace_requests"
  | "marketplace_counter"
  // Agent verification decision (SILL-500) — deep-links to the pro's own
  // marketplace supply profile where the verification card / resubmit lives.
  | "marketplace_profile"
  // Safety review complete (M11-B1, plan 324) — deep-links to the messages
  // workspace. No target id: a review can conclude on a pair that never had a
  // thread, so the list is the only always-real target.
  | "messages";

/** Matches proinbox.ActionRef — null on the wire when no deep-link target. */
export interface NotificationActionRef {
  kind: NotificationActionKind;
  target_id: string;
}

/** Matches proinbox.StateClass */
export type NotificationStateClass = "failure" | "info" | "action_required";

/** Matches proinbox.ReadState */
export type NotificationReadState = "unread" | "seen" | "read";

/** Matches proinbox.Urgency */
export type NotificationUrgency = "realtime" | "digest";

/**
 * One notification row — matches notificationDTO on the wire exactly.
 * Fields: id, type, urgency, state_class, content, action (null = no tap),
 *         read_state, created_at, seen_at, read_at.
 */
export interface NotificationItem {
  id: string;
  type: string;
  urgency: NotificationUrgency;
  state_class: NotificationStateClass;
  content: NotificationContent;
  /** null when the row has no deep-link target. */
  action: NotificationActionRef | null;
  read_state: NotificationReadState;
  dedupe_key?: string;
  created_at: string;
  seen_at?: string | null;
  read_at?: string | null;
}

/** Wire shape for GET /api/professional/notifications */
export interface NotificationsListResponse {
  notifications: NotificationItem[];
  next_cursor?: string;
}

/** Wire shape for GET /api/professional/notifications/unread-count */
export interface NotificationsUnreadCountResponse {
  unread_count: number;
}

/** GET /api/professional/notifications — newest-first, paged. */
export async function listNotifications(
  opts: { cursor?: string; limit?: number } = {},
  token?: string,
  signal?: AbortSignal,
): Promise<NotificationsListResponse> {
  const params = new URLSearchParams();
  if (opts.cursor) params.set("cursor", opts.cursor);
  if (opts.limit) params.set("limit", String(opts.limit));
  const qs = params.toString() ? `?${params.toString()}` : "";
  return getJSON<NotificationsListResponse>(
    `/api/professional/notifications${qs}`,
    token,
    signal,
  );
}

/** GET /api/professional/notifications/unread-count */
export async function getNotificationsUnreadCount(
  token?: string,
  signal?: AbortSignal,
): Promise<number> {
  const res = await getJSON<NotificationsUnreadCountResponse>(
    "/api/professional/notifications/unread-count",
    token,
    signal,
  );
  return res.unread_count;
}

/** POST /api/professional/notifications/seen — legacy bulk seen transition. */
export function markNotificationsSeen(
  token?: string,
  signal?: AbortSignal,
): Promise<void> {
  return postJSON<void>(
    "/api/professional/notifications/seen",
    {},
    token,
    signal,
  );
}

/** POST /api/professional/notifications/:id/read — mark one row read. */
export function markNotificationRead(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<void> {
  return postJSON<void>(
    `/api/professional/notifications/${id}/read`,
    {},
    token,
    signal,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Automations types + API (av2.1 / M2)
//
// Wire shapes mirror the backend dto.go + professional_automations.go exactly.
// The #1 v1 bug: FE sent send_mode/delay_days/notify_type while backend expected
// kind/send_mode/delay_days/notify_type/tag — see dto.go §StepDTO.
// ─────────────────────────────────────────────────────────────────────────────

/** Discriminator values match backend dto.go StepDTO.Kind. */
export type StepKind = "send" | "delay" | "notify" | "tag";

/** Send mode — matches backend automations.SendMode. */
export type SendMode = "auto" | "draft";

/**
 * NotifyStepChannels selects which channels a Notify step delivers on —
 * matches backend automations.NotifyChannels exactly (plan 397). Absent on
 * read means the backend's DefaultNotifyChannels ({in_app:true, email:true,
 * sms:false}); required on every notify step written from this app (see
 * rail-editor-state.ts's editorStepsToDtos, which always emits it).
 */
export interface NotifyStepChannels {
  in_app: boolean;
  email: boolean;
  sms: boolean;
}

/**
 * StepDTO is the HTTP wire shape for a single step (symmetric read/write).
 * Field presence by kind:
 *   send:   kind + template_id + send_mode
 *   delay:  kind + delay_days
 *   notify: kind + notify_type + channels
 *   tag:    kind + tag
 *
 * CRITICAL: these exact field names (kind, template_id, send_mode, delay_days,
 * notify_type, tag, channels) must be used on both read and write — any rename
 * reintroduces the v1 drift bug.
 */
export interface StepDTO {
  kind: StepKind;
  // send
  template_id?: string;
  send_mode?: SendMode;
  // delay
  delay_days?: number;
  // notify
  notify_type?: string;
  channels?: NotifyStepChannels;
  // tag
  tag?: string;
}

/** Classification derived from steps — drives UI chip ("N steps · M sends"). */
export interface AutomationClassification {
  is_sequence: boolean;
  send_count: number;
  has_delay: boolean;
}

/** Enrollment aggregate counts. */
export interface EnrollmentStepBucket {
  recipe_revision: number;
  recipe_fingerprint: string;
  step_index: number;
  step_key: string;
  step_kind: "send" | "delay" | "notify" | "tag" | "unknown" | string;
  step_label: string;
  status: "active" | "paused";
  next_send_label?: string;
  next_send_day?: number;
  count: number;
}

export interface EnrollmentSummary {
  active: number;
  paused: number;
  exited: number;
  completed: number;
  total?: number;
  active_enrollment_count?: number;
  step_buckets?: EnrollmentStepBucket[];
}

/** Automation lifecycle status — matches backend automations.Status. */
export type AutomationStatus = "draft" | "active" | "paused" | "archived";

/** One automation row — matches automationDTO on the wire. */
export interface Automation {
  id: string;
  professional_id: string;
  role: string;
  trigger: string;
  /** Pro-editable display name; empty = FE falls back to a derived label. */
  name: string;
  status: AutomationStatus;
  shadow: boolean;
  recipe_revision?: number;
  recipe_fingerprint?: string;
  steps: StepDTO[];
  classification: AutomationClassification;
  enrollment_summary: EnrollmentSummary;
}

/** Wire shape for GET /api/professional/automations. */
export interface AutomationsListResponse {
  automations: Automation[];
}

/** Body for POST /api/professional/automations. */
export interface CreateAutomationInput {
  trigger: string;
  name?: string;
  steps: StepDTO[];
  exits?: object[];
  repeat_policy?: object;
  shadow?: boolean;
}

/** Body for PATCH /api/professional/automations/:id. */
export interface PatchAutomationInput {
  trigger?: string;
  name?: string;
  steps?: StepDTO[];
}

/** A single publish-preflight blocker — PINNED wire shape (av21-val-blocker-shape). */
export interface PublishBlocker {
  code: string;
  message: string;
  step_index?: number;
}

/** Publish response — PINNED: always 200; callers check .ok not HTTP status. */
export interface PublishResult {
  ok: boolean;
  blockers: PublishBlocker[];
}

/** One entry in the trigger catalog. */
export interface TriggerCatalogEntry {
  trigger: string;
  label: string;
  description: string;
  action_types: string[];
}

// ── API functions ──────────────────────────────────────────────────────────

/** GET /api/professional/automations */
export async function listAutomations(
  token?: string,
  signal?: AbortSignal,
): Promise<Automation[]> {
  const res = await getJSON<AutomationsListResponse>(
    "/api/professional/automations",
    token,
    signal,
  );
  return res.automations ?? [];
}

/** GET /api/professional/automations/:id */
export function getAutomation(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<Automation> {
  return getJSON<Automation>(
    `/api/professional/automations/${id}`,
    token,
    signal,
  );
}

/** POST /api/professional/automations — create draft. */
export function createAutomation(
  input: CreateAutomationInput,
  token?: string,
  signal?: AbortSignal,
): Promise<Automation> {
  return postJSON<Automation>(
    "/api/professional/automations",
    input,
    token,
    signal,
  );
}

/** PATCH /api/professional/automations/:id */
export function patchAutomation(
  id: string,
  input: PatchAutomationInput,
  token?: string,
  signal?: AbortSignal,
): Promise<Automation> {
  return patchJSON<Automation>(
    `/api/professional/automations/${id}`,
    input,
    token,
    signal,
  );
}

/** DELETE /api/professional/automations/:id — archives (terminal). */
export function archiveAutomation(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<void> {
  return deleteJSON<void>(`/api/professional/automations/${id}`, token, signal);
}

/** POST /api/professional/automations/:id/publish */
export function publishAutomation(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<PublishResult> {
  return postJSON<PublishResult>(
    `/api/professional/automations/${id}/publish`,
    {},
    token,
    signal,
  );
}

/** POST /api/professional/automations/:id/duplicate */
export function duplicateAutomation(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<Automation> {
  return postJSON<Automation>(
    `/api/professional/automations/${id}/duplicate`,
    {},
    token,
    signal,
  );
}

/** POST /api/professional/automations/:id/pause */
export function pauseAutomation(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<void> {
  return postJSON<void>(
    `/api/professional/automations/${id}/pause`,
    {},
    token,
    signal,
  );
}

/** POST /api/professional/automations/:id/resume */
export function resumeAutomation(
  id: string,
  token?: string,
  signal?: AbortSignal,
): Promise<void> {
  return postJSON<void>(
    `/api/professional/automations/${id}/resume`,
    {},
    token,
    signal,
  );
}

/** GET /api/professional/automation-triggers */
export async function listTriggerCatalog(
  token?: string,
  signal?: AbortSignal,
): Promise<TriggerCatalogEntry[]> {
  const res = await getJSON<{ triggers: TriggerCatalogEntry[] }>(
    "/api/professional/automation-triggers",
    token,
    signal,
  );
  return res.triggers ?? [];
}

// ── Book capacity (contact-cap upgrade surfaces) ─────────────────────────────

/**
 * BookCapacityView — the pro's contact-cap position. `max_clients: -1` means
 * unlimited (Pro plan / capacity unavailable): every capacity surface
 * (banner, roster meter) renders nothing in that case. The count is the
 * agent-added contact population the cap meters (marketplace-connected
 * people excluded), NOT the whole roster row count.
 */
export interface BookCapacityView {
  max_clients: number;
  agent_added_count: number;
}

/** GET /api/professional/book-capacity */
export function getBookCapacity(
  token?: string,
  signal?: AbortSignal,
): Promise<BookCapacityView> {
  return getJSON<BookCapacityView>(
    "/api/professional/book-capacity",
    token,
    signal,
  );
}
